import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { TENANT_CONST } from '@/constant';
import { useTenantUpdatePlanMutation } from '@/hook';
import {
  VStack,
  VTextButton,
  useUnleash,
  useVModal,
  useVToast,
} from '@/package';
import { useTenantStore } from '@/store';
import type { Tenant } from '@/type';

import { ChangePlanModalContent } from './ChangePlanModalContent';

type ChangePlanButton = {};

export const ChangePlanButton = (_props: ChangePlanButton) => {
  const { t } = useTranslation();
  const { fetchTenantList, getCurrentTenant } = useTenantStore();
  const { isEnabled } = useUnleash();
  const { openAlert, openConfirm } = useVModal();
  const { addToast } = useVToast();
  const { mutateAsync: updatePlan, isPending } = useTenantUpdatePlanMutation();
  const billingUnitRef = useRef<Tenant['billingUnit']>(1);

  const currentTenant = getCurrentTenant();
  const isBIPAVerifiedTenant = isEnabled('vuex.sapporo.bipa');

  const init = () => {
    billingUnitRef.current = currentTenant?.billingUnit ?? 1;
  };

  const updateTenantPlan = () => {
    if (billingUnitRef.current === currentTenant?.billingUnit) return;

    updatePlan({
      billingUnit: billingUnitRef.current,
      tier: TENANT_CONST.TIER.STANDARD,
    }).then(() => {
      setTimeout(() => fetchTenantList(), 5000);

      addToast({
        message: t('admin.changePlanSuccessMessage.haemamoeba'),
        autoClose: true,
        variant: 'success',
      });
    });
  };

  const onChangePlanClick = () => {
    if (currentTenant && isBIPAVerifiedTenant) {
      openConfirm({
        title: t('admin.changePlaneButtonText.hummingbird'),
        message: () => (
          <ChangePlanModalContent
            tenant={currentTenant}
            onBillingUnitChange={billingUnit => {
              billingUnitRef.current = billingUnit;
            }}
          />
        ),
        size: 'medium',
        confirmText: t('admin.changePlaneButtonText.hummingbird'),
        onConfirm() {
          updateTenantPlan();
        },
        onCancel() {
          init();
        },
      });

      return;
    }

    openAlert({
      title: t('admin.changePlaneButtonText.hummingbird'),
      message: () => (
        <VStack
          className="w-full whitespace-pre-line"
          gap={0.25}
          dangerouslySetInnerHTML={{
            __html: t('admin.changePlanModalMessage.orangeist'),
          }}
        />
      ),
      size: 'small',
      confirmText: t('modal.confirmButtonText.vanilla'),
    });
  };

  useEffect(() => {
    if (!currentTenant) return;

    init();
  }, [currentTenant]);

  return isBIPAVerifiedTenant ? (
    <></>
  ) : (
    <>
      <VTextButton disabled={isPending} onClick={onChangePlanClick}>
        {t('admin.changePlaneButtonText.hummingbird')}
      </VTextButton>
    </>
  );
};

ChangePlanButton.displayName = 'ChangePlanButton';
